import { useMemo } from 'react';

import { Select } from '../../base/Select';
import { DeviceStatusBar } from './DeviceStatusBar';
import { useApplianceLookups } from '../../../hooks/useApplianceLookups';
import { IAppliance } from '../../../types/Appliance/Appliance';

export type LiveReadingsViewAs = 'customer' | 'installer' | 'superadmin';

interface ApplianceSettingsProps {
  appliances: IAppliance[];
  selectedAppliance: IAppliance;
  onApplianceChange: (e: React.FormEvent<HTMLSelectElement>) => void;
}

export function ApplianceSettings({
  appliances,
  selectedAppliance,
  onApplianceChange,
}: ApplianceSettingsProps) {
  const { manufacturerLookup, modelLookup, typeLookup } = useApplianceLookups({
    typeId: selectedAppliance.hardwareTypeId,
    manufacturerId: selectedAppliance.hardwareManufacturerId,
  });

  const type = useMemo(
    () => typeLookup(selectedAppliance.hardwareTypeId)?.name,
    [selectedAppliance.hardwareTypeId, typeLookup]
  );

  const manufacturer = useMemo(
    () => manufacturerLookup(selectedAppliance.hardwareManufacturerId)?.name,
    [selectedAppliance.hardwareManufacturerId, manufacturerLookup]
  );

  const model = useMemo(
    () => modelLookup(selectedAppliance.hardwareModelId)?.name,
    [selectedAppliance.hardwareModelId, modelLookup]
  );

  return (
    <div>
      <div className="w-full block space-y-4 md:space-y-0 md:mb-0 md:flex md:space-x-4 md:items-center">
        <div className="w-full block md:mb-0 md:flex md:space-x-4 md:items-center">
          <p className="text-sm mb-2 md:mb-0">Appliance</p>
          <Select className="w-full md:w-64" onChange={onApplianceChange}>
            {appliances.map((appliance) => (
              <option key={appliance.id} value={appliance.id}>
                {appliance.name}
              </option>
            ))}
          </Select>
          {type && manufacturer && model && (
            <p className="my-4 text-center md:my-0">
              {`${type} | ${manufacturer} | ${model}`}
            </p>
          )}
        </div>
        <DeviceStatusBar appliance={selectedAppliance} />
      </div>
    </div>
  );
}
